import React, { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import { Close as CloseIcon, CheckCircle as CheckCircleIcon, PlayArrow as PlayIcon, Pause as PauseIcon, UploadFile as UploadFileIcon } from "@mui/icons-material";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Tooltip from "@mui/material/Tooltip";
import "../pubvision/AssetsGeneration.css";
import PropTypes from "prop-types";


const BASE_URL = process.env.REACT_APP_BASE_URL;

const MUSIC_TRACKS = [
  {
    id: "corporate",
    name: "Corporate Tech",
    description: "Clean, professional, and ambient synthesizer track (Local Asset)",
    url: "/music/corporate.mp3",
  },
  {
    id: "science",
    name: "Inspiring Science",
    description: "Inspiring acoustic guitar & uplifting orchestral strings (Local Asset)",
    url: "/music/science.mp3",
  },
  {
    id: "calm",
    name: "Calm & Focused",
    description: "Gentle piano pads and peaceful academic atmosphere (Local Asset)",
    url: "/music/calm.mp3",
  },
  {
    id: "energetic",
    name: "Energetic Modern",
    description: "Upbeat electronic rhythm with motivational energy (Local Asset)",
    url: "/music/energetic.mp3",
  },
  {
    id: "none",
    name: "No Music",
    description: "Generate video without any background music",
    url: null,
  }
];

const AbstractVideoDialog = ({
  open,
  onClose,
  videoDuration,
  setVideoDuration,
  language,
  setLanguage,
  selectedColor,
  setSelectedColor,
  customColor,
  setCustomColor,
  onFileChange,
  onStartGenerating,
  contentLevel,
  setContentLevel,
  publicationId,
  videoId,
  selectedSections,
  setSelectedSections,
  setActiveTasks,
  setTaskStatus,
  setRefreshTrigger,
}) => {


  const levels = [
    "Physicians",
    "Medical Affairs",
    "Clinical Development",
    "Commercial Teams",
    "Market Access & HEOR",
    "Regulatory & Safety",
    "KOLs / Researchers",
    "Executives",
    "Patients"
  ];

  const CREDITS_REQUIRED = 10; // Credits required for Video generation

  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [sectionsError, setSectionsError] = useState(null);
  // true only when templates came from an already-TemplatesSelected project (existing) OR
  // the full task completed to 100% (backend set status=TemplatesSelected).
  // Must NOT be true just because a fresh task started (status=queued) and we closed early.
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessages, setProgressMessages] = useState([]);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoName, setLogoName] = useState("");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const wsRef = useRef(null);

  // Reset sectionsLoaded and other state every time the dialog opens (fresh session)
  useEffect(() => {
    if (open) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setProgress(0);
      setProgressMessages([]);
      setSectionsError(null);
      setLoadingSections(false);
      setSectionsLoaded(false);
      setSections([]);
      setSelectedLanguage("");
      setSelectedParsedAccent("");
      setSelectedAccent("");
      setSelectedVoice("");

      if (!videoDuration) {
        setVideoDuration("detailed");
      }

      if (videoId) {
        setCurrentVideoId(videoId);
        handleFetchSections();
      } else {
        setCurrentVideoId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [playingTrackId, setPlayingTrackId] = useState(null);
  const audioRef = useRef(null);

  const [backgroundMusicList, setBackgroundMusicList] = useState([]);
  const [voices, setVoices] = useState({});
  const [parsedLanguages, setParsedLanguages] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedParsedAccent, setSelectedParsedAccent] = useState("");
  const [selectedAccent, setSelectedAccent] = useState("");
  const [selectedVoice, setSelectedVoice] = useState("");
  const [selectedMusic, setSelectedMusic] = useState("");

  useEffect(() => {
    const fetchMediaOptions = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${BASE_URL}/get_all_voices`, {
          method: "GET",
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.voiceOvers) {
            setVoices(data.voiceOvers);
            const parsed = {};
            Object.keys(data.voiceOvers).forEach(key => {
              const accentData = data.voiceOvers[key];
              const display = accentData.display_accent_name || key;
              const match = display.match(/^(.+?)\s*\((.+?)\)$/);
              let lang = "English";
              let acc = display;
              if (match) {
                lang = match[1].trim();
                acc = match[2].trim();
              } else if (display.includes(" ")) {
                lang = display.split(" ")[0];
                acc = display.substring(display.indexOf(" ") + 1);
              } else {
                lang = display;
                acc = "Default";
              }
              if (!parsed[lang]) parsed[lang] = { accents: {} };
              parsed[lang].accents[acc] = { key, Male: accentData.Male, Female: accentData.Female };
            });
            setParsedLanguages(parsed);

            const langs = Object.keys(parsed);
            if (langs.length > 0) {
              const defaultLang = langs[0];
              setSelectedLanguage(defaultLang);
              const accs = Object.keys(parsed[defaultLang].accents);
              if (accs.length > 0) {
                const defaultAcc = accs[0];
                setSelectedParsedAccent(defaultAcc);
                const accentData = parsed[defaultLang].accents[defaultAcc];
                setSelectedAccent(accentData.key);
                if (accentData.Male?.length > 0) {
                  setSelectedVoice(accentData.Male[0].id);
                } else if (accentData.Female?.length > 0) {
                  setSelectedVoice(accentData.Female[0].id);
                }
              }
            }
          }
          if (data.backgroundMusic) {
            setBackgroundMusicList(data.backgroundMusic);
            if (data.backgroundMusic.length > 0) setSelectedMusic(data.backgroundMusic[0].id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch voices and music", error);
      }
    };
    if (open) {
      fetchMediaOptions();
    }
  }, [open]);

  // Handle dialog opening and resetting state change or unmount
  useEffect(() => {
    if (!open) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingTrackId(null);
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [open]);

  const handleTogglePlay = (e, track) => {
    e.preventDefault();
    e.stopPropagation();

    if (playingTrackId === track.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        setPlayingTrackId(null);
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const newAudio = new Audio(track.sample_audio_path || track.url);
      newAudio.play().catch(err => console.error("Failed to play track:", err));
      newAudio.onended = () => setPlayingTrackId(null);
      audioRef.current = newAudio;
      setPlayingTrackId(track.id);
    }
  };

  // Content descriptions for each understanding level
  const levelDescriptions = {
    "Physicians": "Frontline clinicians focused on patient care, diagnosis, treatment decisions, and clinical outcomes.",
    "Medical Affairs": "Bridge between clinical data and the medical community — managing publications, advisory boards, and scientific exchange.",
    "Clinical Development": "Designs and runs clinical trials to evaluate safety, efficacy, and dosing of new therapies.",
    "Commercial Teams": "Drive product adoption through sales strategy, marketing, and customer engagement with healthcare providers.",
    "Market Access & HEOR": "Secure payer coverage and demonstrate value through health economics, outcomes research, and reimbursement strategy.",
    "Regulatory & Safety": "Ensure compliance with regulatory requirements, manage drug safety reporting, and oversee pharmacovigilance.",
    "KOLs / Researchers": "Academic leaders and researchers who shape clinical practice through publications, guidelines, and thought leadership.",
    "Executives": "Senior leadership driving corporate strategy, pipeline decisions, and organizational priorities.",
    "Patients": "Individuals receiving medical care, seeking to understand disease states, treatments, and clinical outcomes."
  };

  // Progress-based messages with their thresholds
  const progressSteps = [
    { threshold: 0, text: "Processing your publication…" },
    { threshold: 10, text: "Detecting document structure and section boundaries…" },
    { threshold: 20, text: "Extracting the abstract and key metadata…" },
    { threshold: 30, text: "Parsing the introduction to understand research context…" },
    { threshold: 40, text: "Analyzing study design and methodology…" },
    { threshold: 50, text: "Extracting results, figures, and statistical findings…" },
    { threshold: 60, text: "Reviewing discussion and clinical implications…" },
    { threshold: 70, text: "Indexing references, tables, and supplementary material…" },
    { threshold: 80, text: "Structuring insights for intelligent search and retrieval…" },
    { threshold: 90, text: "Preparing the publication for interactive analysis…" }
  ];

  // Function to update messages based on progress
  const updateProgressMessages = (currentProgress) => {
    const newMessages = [];

    progressSteps.forEach((step) => {
      if (currentProgress >= step.threshold) {
        newMessages.push({
          text: step.text,
          completed: currentProgress > step.threshold + 5
        });
      }
    });

    if (newMessages.length > 0 && currentProgress < 100) {
      newMessages.at(-1).completed = false;
    }

    setProgressMessages(newMessages);
  };



  // ── STEP 1 action: called when user clicks "Next" (or similar) ──────────────────
  const handleFetchSections = async () => {
    const token = localStorage.getItem("authToken");
    setSectionsError(null);
    setLoadingSections(true);
    setProgress(0);
    setProgressMessages([]);

    try {
      // Step 1: First check if sections already exist (for refresh scenarios)
      if (videoId) {
        console.log("[AbstractVideoDialog] Checking if sections already exist for videoId:", videoId);
        const checkResponse = await fetch(
          `${BASE_URL}/get_video_templates?videoId=${videoId}`,
          {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (checkResponse.ok) {
          const existingTemplates = await checkResponse.json();
          console.log("[AbstractVideoDialog] Existing templates check:", existingTemplates);

          // If sections already exist, use them — this project is already TemplatesSelected
          if (Array.isArray(existingTemplates) && existingTemplates.length > 0) {
            console.log("[AbstractVideoDialog] Using existing sections:", existingTemplates);
            setSections(existingTemplates);
            // Auto-select all sections by default
            setSelectedSections(existingTemplates.map(section => {
              const templateSource = section.templates?.[0] || section.llm_recommendation?.[0];
              const templateIds = templateSource?.template_id || [];
              return {
                section_id: section.section_id,
                section_name: section.section_name,
                template_ids: Array.isArray(templateIds) ? templateIds : [templateIds]
              };
            }));
            // Mark as loaded — this videoId already has TemplatesSelected status on the backend
            setSectionsLoaded(true);
            setLoadingSections(false);
            return; // Exit early - no need to extract again
          }
        }
      } else {
        console.log("[AbstractVideoDialog] No videoId provided, skipping templates check and proceeding to extraction.");
      }

      // Step 2: If no existing sections, initiate extraction
      console.log("[AbstractVideoDialog] No existing sections found, initiating extraction...");
      console.log("[AbstractVideoDialog] Calling get_sections_with_templates...");
      const voiceParam = selectedVoice ? `&voiceId=${selectedVoice}` : "";
      const musicParam = selectedMusic ? `&backgroundMusicId=${selectedMusic}` : "";
      const response = await fetch(
        `${BASE_URL}/get_sections_with_templates?publicationId=${publicationId}&professionalType=${contentLevel}${voiceParam}${musicParam}`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to initiate section extraction");
      }

      const data = await response.json();
      console.log("[AbstractVideoDialog] Received task_id:", data.task_id);

      if (!data.task_id) {
        throw new Error("No task_id received");
      }

      if (data.videoId) {
        setCurrentVideoId(data.videoId);

        if (setActiveTasks && setTaskStatus) {
          setActiveTasks((prev) => {
            const nextArr = [...(prev.video || [])];
            if (!nextArr.includes(data.videoId)) nextArr.push(data.videoId);
            return { ...prev, video: nextArr };
          });
          localStorage.setItem("activeTasks", "true");
          setTaskStatus((prev) => ({
            ...prev,
            video: {
              ...(prev.video || {}),
              [data.videoId]: {
                progress: 0,
                status: "queued",
                publicationId: Number(publicationId),
                video_project_id: data.videoId,
              },
            },
          }));
          if (setRefreshTrigger) {
            setRefreshTrigger(prev => prev + 1);
          }
        }
      }

      // Step 3: Setup WebSocket connection
      const wsBaseUrl = process.env.REACT_APP_WS_BASE_URL;
      const wsUrl = `${wsBaseUrl}/taskStatus/${data.task_id}`;
      console.log("[AbstractVideoDialog] Connecting to WebSocket:", wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("[AbstractVideoDialog] WebSocket connected");
      };

      ws.onmessage = async (event) => {
        const wsData = JSON.parse(event.data);
        console.log("[AbstractVideoDialog] WebSocket message:", wsData);

        const currentProgress = wsData.progress || 0;
        setProgress(currentProgress);

        // Update messages based on current progress
        updateProgressMessages(currentProgress);

        // Handle completion
        if (currentProgress >= 100) {
          console.log("[AbstractVideoDialog] Progress complete, fetching sections...");

          // Mark all messages as complete
          setProgressMessages(prev =>
            prev.map(msg => ({ ...msg, completed: true }))
          );

          // Close WebSocket
          ws.close();

          // Step 4: Call get_video_templates endpoint
          try {
            const templatesResponse = await fetch(
              `${BASE_URL}/get_video_templates?videoId=${data.videoId}`,
              {
                method: "GET",
                headers: {
                  "Authorization": `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (!templatesResponse.ok) {
              throw new Error("Failed to fetch video templates");
            }

            const templatesData = await templatesResponse.json();
            console.log("[AbstractVideoDialog] Templates received:", templatesData);

            // Store the sections
            if (Array.isArray(templatesData)) {
              setSections(templatesData);
              // Auto-select all sections by default
              setSelectedSections(templatesData.map(section => {
                const templateSource = section.templates?.[0] || section.llm_recommendation?.[0];
                const templateIds = templateSource?.template_id || [];
                return {
                  section_id: section.section_id,
                  section_name: section.section_name,
                  template_ids: Array.isArray(templateIds) ? templateIds : [templateIds]
                };
              }));
              // Task completed to 100% → backend now has status=TemplatesSelected
              setSectionsLoaded(true);
            }

            setLoadingSections(false);
          } catch (error) {
            console.error("[AbstractVideoDialog] Error fetching templates:", error);
            setSectionsError("Failed to load video templates");
            setLoadingSections(false);
          }
        }

        // Handle errors
        if (wsData.error || currentProgress < 0) {
          console.error("[AbstractVideoDialog] WebSocket error:", wsData.error);
          setSectionsError("An error occurred while extracting sections");
          setLoadingSections(false);
          ws.close();
        }
      };

      ws.onerror = (error) => {
        console.error("[AbstractVideoDialog] WebSocket error:", error);
        setSectionsError("Connection error. Please try again.");
        setLoadingSections(false);
      };

      ws.onclose = () => {
        console.log("[AbstractVideoDialog] WebSocket closed");
      };

    } catch (error) {
      console.error("[AbstractVideoDialog] Error:", error);
      setSectionsError("Failed to initialize section extraction");
      setLoadingSections(false);
    }
  };

  const handleSectionChange = (section) => {
    setSelectedSections((prev) => {
      const isSelected = prev.some(s => s.section_id === section.section_id);
      if (isSelected) {
        return prev.filter((s) => s.section_id !== section.section_id);
      } else {
        const templateSource = section.templates?.[0] || section.llm_recommendation?.[0];
        const templateIds = templateSource?.template_id || [];
        return [...prev, {
          section_id: section.section_id,
          section_name: section.section_name,
          template_ids: Array.isArray(templateIds) ? templateIds : [templateIds]
        }];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedSections.length === sections.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(sections.map(section => {
        const templateSource = section.templates?.[0] || section.llm_recommendation?.[0];
        const templateIds = templateSource?.template_id || [];
        return {
          section_id: section.section_id,
          section_name: section.section_name,
          template_ids: Array.isArray(templateIds) ? templateIds : [templateIds]
        };
      }));
    }
  };

  const isSectionSelected = (section) => {
    return selectedSections.some(s => s.section_id === section.section_id);
  };

  const handleGenerateClick = () => {
    console.log("[AbstractVideoDialog] Start Generating button clicked.");
    console.log("[AbstractVideoDialog] Selected sections:", selectedSections);
    console.log("[AbstractVideoDialog] Selected music:", selectedMusic);
    onStartGenerating(selectedMusic, currentVideoId);
  };

  const handleCloseDialog = () => {
    if (onClose) {
      // Pass sectionsLoaded (not sections.length > 0) — only true when the backend has
      // actually set status=TemplatesSelected (either pre-existing or task hit 100%).
      // This prevents the 'Resume' button from showing when user closes during a fresh task.
      onClose(currentVideoId, sectionsLoaded);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      className="abstract-video-dialog"
      sx={{
        "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        "& .MuiPaper-root": { fontFamily: "'Inter', sans-serif" },
        "& *": { fontFamily: "'Inter', sans-serif !important" },
      }}
    >
      <DialogTitle className="dialog-title">
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          className="close-button"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogTitle className="dialog-title main-title">
        <strong>Video: Enter requirements below</strong>
      </DialogTitle>

      <div className="dialog-subtitle-container">
        <p className="dialog-subtitle">
          Please fill the form below to get the Video as per your branding
        </p>
      </div>

      <DialogContent className="dialog-content">
        {!sectionsLoaded && (
          <>
            {!loadingSections && (
              <>
                <div style={{ padding: "20px", border: "1px solid #e2e8f0", borderRadius: "12px", marginBottom: "20px" }}>
                  <div style={{ display: "flex", gap: "24px", flexDirection: "row", flexWrap: "wrap" }}>
                    <div className="dialog-field" style={{ flex: 1, minWidth: "250px" }}>
                      <FormControl component="fieldset" style={{ width: "100%" }}>
                        <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "12px" }}>
                          Choose Content Understanding Level
                        </FormLabel>
                        <Select
                          value={contentLevel}
                          onChange={(e) => {
                            console.log("[AbstractVideoDialog] contentLevel changed to:", e.target.value);
                            setContentLevel(e.target.value);
                          }}
                          displayEmpty
                          className="level-options"
                          style={{ width: "100%" }}
                          disabled={loadingSections}
                          sx={{
                            borderRadius: "12px",
                            height: "46px"
                          }}
                        >
                          {levels.map((level) => (
                            <MenuItem key={level} value={level} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span>{level}</span>
                              <Tooltip
                                title={levelDescriptions[level]}
                                arrow
                                placement="right"
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      backgroundColor: "#fff",
                                      color: "#1E4DE3",
                                      fontSize: "13px",
                                      lineHeight: "1.5",
                                      padding: "10px 14px",
                                      maxWidth: "350px",
                                      border: "1px solid #1E4DE3",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                      "& .MuiTooltip-arrow": {
                                        color: "#fff",
                                        "&::before": {
                                          border: "1px solid #1E4DE3"
                                        }
                                      }
                                    }
                                  }
                                }}
                              >
                                <IconButton
                                  size="small"
                                  style={{
                                    padding: "4px",
                                    color: "#9ca3af",
                                    transition: "all 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "scale(1.1)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "scale(1)";
                                  }}
                                >
                                  <AiOutlineInfoCircle size={16} />
                                </IconButton>
                              </Tooltip>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    {/* Choose Video Duration */}
                    <div className="dialog-field" style={{ flex: 1, minWidth: "250px" }}>
                      <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "12px" }}>
                        Choose Video Duration
                      </FormLabel>
                      <div style={{ display: "flex", gap: "16px" }}>
                        {[
                          { value: "short", label: "Short" },
                          { value: "detailed", label: "Detailed" },
                        ].map((opt) => {
                          const isSelected = videoDuration === opt.value;
                          return (
                            <div
                              key={opt.value}
                              onClick={() => {
                                console.log("[AbstractVideoDialog] videoDuration:", opt.value);
                                setVideoDuration(opt.value);
                              }}
                              style={{
                                padding: "10px 32px",
                                border: isSelected ? "2px solid #1E4DE3" : "1px solid #e0e0e0",
                                borderRadius: "12px",
                                backgroundColor: isSelected ? "#f0f4ff" : "#fff",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s ease",
                                boxShadow: isSelected ? "0 4px 12px rgba(30, 77, 227, 0.15)" : "none",
                                transform: isSelected ? "translateY(-2px)" : "none",
                                flex: 1
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = "#b3c4f7";
                                  e.currentTarget.style.backgroundColor = "#f8faff";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.borderColor = "#e0e0e0";
                                  e.currentTarget.style.backgroundColor = "#fff";
                                }
                              }}
                            >
                              <span style={{
                                fontSize: "15px",
                                fontWeight: isSelected ? 700 : 500,
                                color: isSelected ? "#1E4DE3" : "#333",
                                textAlign: "center"
                              }}>
                                {opt.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {loadingSections && (
              <>
                <div style={{
                  fontSize: "13px",
                  color: "#666",
                  fontStyle: "italic",
                  marginTop: "8px",
                  marginBottom: "8px"
                }}>
                  Note: Takes approx 4 min. You can safely close window and come back.
                </div>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: "24px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                  marginTop: "10px",
                  gap: "16px",
                  width: "100%",
                  boxSizing: "border-box"
                }}>
                  <div style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px"
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%"
                    }}>
                      <span style={{
                        color: "#1E4DE3",
                        fontSize: "15px",
                        fontWeight: 600
                      }}>
                        Extracting Sections
                      </span>
                      <span style={{
                        color: "#1E4DE3",
                        fontSize: "15px",
                        fontWeight: 700
                      }}>
                        {progress}%
                      </span>
                    </div>

                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#1E4DE3",
                          borderRadius: 4
                        }
                      }}
                    />
                  </div>

                  {progressMessages.length > 0 && (
                    <div style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      marginTop: "8px"
                    }}>
                      {progressMessages.map((msg, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                            padding: "8px 0"
                          }}
                        >
                          {msg.completed ? (
                            <CheckCircleIcon
                              sx={{
                                color: "#1E4DE3",
                                fontSize: "20px",
                                marginTop: "2px",
                                flexShrink: 0
                              }}
                            />
                          ) : (
                            <CircularProgress
                              size={16}
                              sx={{
                                color: "#1E4DE3",
                                marginTop: "2px",
                                marginLeft: "2px",
                                marginRight: "2px",
                                flexShrink: 0
                              }}
                            />
                          )}
                          <span
                            style={{
                              color: msg.completed ? "#1E4DE3" : "#555",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              fontWeight: msg.completed ? 400 : 500
                            }}
                          >
                            {msg.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {sectionsError && (
              <div style={{
                color: "#d32f2f",
                padding: "15px",
                backgroundColor: "#ffebee",
                borderRadius: "8px",
                marginTop: "10px",
                fontSize: "14px"
              }}>
                {sectionsError}
              </div>
            )}
          </>
        )}

        {sectionsLoaded && (
          <>
            {/* Choose Sections */}
            <div className="dialog-field" style={{ width: "100%" }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" className="dialog-label" focused={false}>
                  Choose Sections
                </FormLabel>
                <div style={{ marginTop: "10px" }}>
                  {sections.length > 0 && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedSections.length === sections.length}
                          indeterminate={selectedSections.length > 0 && selectedSections.length < sections.length}
                          onChange={handleSelectAll}
                          sx={{
                            color: "#1E4DE3",
                            "&.Mui-checked": {
                              color: "#1E4DE3",
                            },
                            "&.MuiCheckbox-indeterminate": {
                              color: "#1E4DE3",
                            }
                          }}
                        />
                      }
                      label="Select All"
                      sx={{
                        marginBottom: "8px",
                        "& .MuiFormControlLabel-label": {
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#333"
                        }
                      }}
                    />
                  )}
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    padding: "4px"
                  }}>
                    {sections.map((section) => {
                      const isSelected = isSectionSelected(section);
                      return (
                        <FormControlLabel
                          key={section.section_id}
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSectionChange(section)}
                              sx={{
                                color: "#1E4DE3",
                                "&.Mui-checked": {
                                  color: "#1E4DE3",
                                }
                              }}
                            />
                          }
                          label={section.section_name}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "14px",
                              color: "#555"
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                  {sections.length === 0 && (
                    <div style={{
                      color: "#888",
                      padding: "20px",
                      textAlign: "center",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      fontSize: "14px"
                    }}>
                      No sections available
                    </div>
                  )}
                </div>
              </FormControl>
            </div>
          </>
        )}

        {!sectionsLoaded && !loadingSections && (
          <>
            <div style={{ padding: "20px", border: "1px solid #e2e8f0", borderRadius: "12px", marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                <FormLabel component="legend" className="dialog-label" focused={false} style={{ margin: 0, fontWeight: 600 }}>
                  Audio
                </FormLabel>
              </div>

              <div style={{ display: "flex", gap: "16px", flexDirection: "row", flexWrap: "wrap" }}>
                {/* Choose Language */}
                <div className="dialog-field" style={{ flex: 1, minWidth: "150px" }}>
                  <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px", fontSize: "13px" }}>
                    Choose Language
                  </FormLabel>
                  <FormControl size="small" style={{ width: "100%", marginTop: "4px" }}>
                    <Select
                      value={selectedLanguage}
                      disabled={loadingSections}
                      onChange={(e) => {
                        const newLang = e.target.value;
                        setSelectedLanguage(newLang);
                        if (parsedLanguages[newLang]) {
                          const accs = Object.keys(parsedLanguages[newLang].accents);
                          if (accs.length > 0) {
                            const newAcc = accs[0];
                            setSelectedParsedAccent(newAcc);
                            const accentData = parsedLanguages[newLang].accents[newAcc];
                            setSelectedAccent(accentData.key);
                            if (accentData.Male?.length > 0) setSelectedVoice(accentData.Male[0].id);
                            else if (accentData.Female?.length > 0) setSelectedVoice(accentData.Female[0].id);
                            else setSelectedVoice("");
                          }
                        }
                        if (audioRef.current) {
                          audioRef.current.pause();
                          setPlayingTrackId(null);
                        }
                      }}
                      sx={{
                        borderRadius: "8px", fontSize: "13px",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E4DE3" }
                      }}
                    >
                      {Object.keys(parsedLanguages).map(lang => (
                        <MenuItem key={lang} value={lang} sx={{ fontSize: "13px" }}>{lang}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {/* Choose Accent */}
                <div className="dialog-field" style={{ flex: 1, minWidth: "150px" }}>
                  <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px", fontSize: "13px" }}>
                    Choose Accent
                  </FormLabel>
                  <FormControl size="small" style={{ width: "100%", marginTop: "4px" }}>
                    <Select
                      value={selectedParsedAccent}
                      disabled={loadingSections || !selectedLanguage}
                      onChange={(e) => {
                        const newAcc = e.target.value;
                        setSelectedParsedAccent(newAcc);
                        if (parsedLanguages[selectedLanguage]?.accents[newAcc]) {
                          const accentData = parsedLanguages[selectedLanguage].accents[newAcc];
                          setSelectedAccent(accentData.key);
                          if (accentData.Male?.length > 0) setSelectedVoice(accentData.Male[0].id);
                          else if (accentData.Female?.length > 0) setSelectedVoice(accentData.Female[0].id);
                          else setSelectedVoice("");
                        }
                        if (audioRef.current) {
                          audioRef.current.pause();
                          setPlayingTrackId(null);
                        }
                      }}
                      sx={{
                        borderRadius: "8px", fontSize: "13px",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E4DE3" }
                      }}
                    >
                      {selectedLanguage && parsedLanguages[selectedLanguage] && Object.keys(parsedLanguages[selectedLanguage].accents).map(acc => (
                        <MenuItem key={acc} value={acc} sx={{ fontSize: "13px" }}>{acc}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {/* Choose Voiceover */}
                <div className="dialog-field" style={{ flex: 1, minWidth: "180px" }}>
                  <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px", fontSize: "13px" }}>
                    Choose Voiceover
                  </FormLabel>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", marginTop: "4px" }}>
                    <FormControl size="small" style={{ flex: 1 }}>
                      <Select
                        value={selectedVoice}
                        disabled={loadingSections || !selectedAccent}
                        onChange={(e) => {
                          setSelectedVoice(e.target.value);
                          if (audioRef.current) {
                            audioRef.current.pause();
                            setPlayingTrackId(null);
                          }
                        }}
                        renderValue={(selected) => {
                          let v = voices[selectedAccent]?.Male?.find(t => t.id === selected);
                          if (!v) v = voices[selectedAccent]?.Female?.find(t => t.id === selected);
                          if (!v) return "";
                          const isMale = voices[selectedAccent]?.Male?.find(t => t.id === selected);
                          return `${v.display_name} (${isMale ? 'Male' : 'Female'})`;
                        }}
                        sx={{
                          borderRadius: "8px", fontSize: "13px",
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E4DE3" }
                        }}
                      >
                        {selectedAccent && voices[selectedAccent]?.Male?.length > 0 ? (
                          <ListSubheader sx={{ fontWeight: 'bold', color: '#1E4DE3' }}>Male</ListSubheader>
                        ) : null}
                        {selectedAccent ? (voices[selectedAccent]?.Male || []).map(voice => (
                          <MenuItem key={voice.id} value={voice.id} sx={{ fontSize: "13px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>{voice.display_name} (Male)</span>
                          </MenuItem>
                        )) : null}
                        {selectedAccent && voices[selectedAccent]?.Female?.length > 0 ? (
                          <ListSubheader sx={{ fontWeight: 'bold', color: '#1E4DE3' }}>Female</ListSubheader>
                        ) : null}
                        {selectedAccent ? (voices[selectedAccent]?.Female || []).map(voice => (
                          <MenuItem key={voice.id} value={voice.id} sx={{ fontSize: "13px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>{voice.display_name} (Female)</span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTogglePlay(e, voice);
                              }}
                              sx={{ padding: "2px", color: playingTrackId === voice.id ? "#1E4DE3" : "gray" }}
                            >
                              {playingTrackId === voice.id ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                            </IconButton>
                          </MenuItem>
                        )) : null}
                      </Select>
                    </FormControl>
                    {selectedVoice && selectedAccent && (
                      <button
                        onClick={(e) => {
                          let v = voices[selectedAccent]?.Male?.find(t => t.id === selectedVoice);
                          if (!v) v = voices[selectedAccent]?.Female?.find(t => t.id === selectedVoice);
                          if (v) handleTogglePlay(e, v);
                        }}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          border: "1px solid #1E4DE3", borderRadius: "8px", padding: "0",
                          width: "36px", height: "36px", cursor: "pointer",
                          backgroundColor: playingTrackId === selectedVoice ? "#1E4DE3" : "transparent",
                          color: playingTrackId === selectedVoice ? "#ffffff" : "#1E4DE3",
                          transition: "all 0.15s ease", flexShrink: 0
                        }}
                      >
                        {playingTrackId === selectedVoice ? <PauseIcon style={{ fontSize: "18px" }} /> : <PlayIcon style={{ fontSize: "18px" }} />}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Choose Background Music */}
              <div className="dialog-field" style={{ width: "100%", marginTop: "16px" }}>
                <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px", fontSize: "13px" }}>
                  Choose Background Music
                </FormLabel>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%", marginTop: "4px" }}>
                  <FormControl size="small" style={{ flex: 1 }}>
                    <Select
                      value={selectedMusic}
                      disabled={loadingSections}
                      onChange={(e) => {
                        setSelectedMusic(e.target.value);
                        if (audioRef.current) {
                          audioRef.current.pause();
                          setPlayingTrackId(null);
                        }
                      }}
                      renderValue={(selected) => {
                        const track = backgroundMusicList.find(t => t.id === selected);
                        return track ? (track.display_name || track.name) : "";
                      }}
                      sx={{
                        borderRadius: "8px", fontSize: "13px",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E4DE3" }
                      }}
                    >
                      {backgroundMusicList.map((track) => (
                        <MenuItem key={track.id} value={track.id} sx={{ fontSize: "13px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span>{track.display_name || track.name}</span>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePlay(e, track);
                            }}
                            sx={{ padding: "2px", color: playingTrackId === track.id ? "#1E4DE3" : "gray" }}
                          >
                            {playingTrackId === track.id ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                          </IconButton>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedMusic && selectedMusic !== "none" && (
                    <button
                      onClick={(e) => {
                        const track = backgroundMusicList.find(t => t.id === selectedMusic);
                        if (track) handleTogglePlay(e, track);
                      }}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "1px solid #1E4DE3", borderRadius: "8px", padding: "0",
                        width: "36px", height: "36px", cursor: "pointer",
                        backgroundColor: playingTrackId === selectedMusic ? "#1E4DE3" : "transparent",
                        color: playingTrackId === selectedMusic ? "#ffffff" : "#1E4DE3",
                        transition: "all 0.15s ease", flexShrink: 0
                      }}
                    >
                      {playingTrackId === selectedMusic ? <PauseIcon style={{ fontSize: "18px" }} /> : <PlayIcon style={{ fontSize: "18px" }} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {!sectionsLoaded && !loadingSections && (
          <>
            {/* Theme & Branding */}
            <div style={{ padding: "20px", border: "1px solid #e2e8f0", borderRadius: "12px", marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                <FormLabel component="legend" className="dialog-label" focused={false} style={{ margin: 0, fontWeight: 600 }}>
                  Theme & Branding
                </FormLabel>
              </div>

              <div style={{ display: "flex", gap: "24px", flexDirection: "row", flexWrap: "wrap" }}>
                {/* Choose Primary Color */}
                <div className="dialog-field" style={{ flex: 1, minWidth: "200px" }}>
                  <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px", fontSize: "13px" }}>
                    Choose Primary Color
                  </FormLabel>
                  <div style={{ display: "flex", gap: "12px", alignItems: "center", marginTop: "4px" }}>
                    {[
                      { main: "#1E4DE3", light: "#B3C8FF" },
                      { main: "#16A34A", light: "#A7F3D0" },
                      { main: "#F97316", light: "#FDBA74" },
                      { main: "#9333EA", light: "#D8B4FE" },
                      { main: "#EAB308", light: "#FDE047" },
                    ].map((colorObj) => {
                      const color = colorObj.main;
                      const isSelected = selectedColor === color;
                      return (
                        <div
                          key={color}
                          onClick={() => setSelectedColor(isSelected ? "" : color)}
                          style={{
                            width: "52px",
                            height: "52px",
                            borderRadius: "8px",
                            display: "flex",
                            cursor: "pointer",
                            border: isSelected ? "2px solid #1E4DE3" : "2px solid #e2e8f0",
                            boxShadow: isSelected ? "0 0 0 1px #1E4DE3" : "none",
                            overflow: "hidden",
                            transition: "all 0.2s",
                            position: "relative"
                          }}
                        >
                          <div style={{ flex: 1, backgroundColor: colorObj.main }}></div>
                          <div style={{ flex: 1, backgroundColor: colorObj.light }}></div>
                          {isSelected && (
                            <div style={{ position: "absolute", top: "-4px", right: "-4px", backgroundColor: "#1E4DE3", color: "white", borderRadius: "50%", width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <CheckCircleIcon style={{ fontSize: "12px" }} />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Upload Logo */}
                <div className="dialog-field" style={{ flex: 1, minWidth: "250px" }}>
                  <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px", fontSize: "13px" }}>
                    Upload Logo
                  </FormLabel>
                  <input
                    type="file"
                    id="video-logo-upload"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        const file = e.target.files[0];
                        setLogoName(file.name);
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setLogoPreview(event.target.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  {logoPreview ? (
                    <div style={{
                      border: "1px dashed #e2e8f0",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "#fff",
                      minHeight: "56px",
                      boxSizing: "border-box",
                      cursor: "pointer",
                      transition: "border-color 0.2s"
                    }}
                      onMouseOver={(e) => e.currentTarget.style.borderColor = "#1E4DE3"}
                      onMouseOut={(e) => e.currentTarget.style.borderColor = "#e2e8f0"}
                      onClick={() => setIsPreviewModalOpen(true)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", overflow: "hidden" }}>
                        <div style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "4px",
                          backgroundColor: "#f8fafc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #e2e8f0",
                          overflow: "hidden",
                          flexShrink: 0
                        }}>
                          <img src={logoPreview} alt="Logo Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                          <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "200px" }}>
                            {logoName}
                          </span>
                        </div>
                      </div>
                      <IconButton
                        size="small"
                        style={{ backgroundColor: "#f1f5f9" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLogoPreview(null);
                          setLogoName("");
                          document.getElementById("video-logo-upload").value = "";
                        }}
                      >
                        <CloseIcon style={{ fontSize: "14px", color: "#64748b" }} />
                      </IconButton>
                    </div>
                  ) : (
                    <div style={{
                      position: "relative",
                      border: "1px dashed #cbd5e1",
                      borderRadius: "8px",
                      padding: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      backgroundColor: "#f8fafc",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      minHeight: "56px",
                      boxSizing: "border-box"
                    }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = "#1E4DE3";
                        e.currentTarget.style.backgroundColor = "#eff6ff";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = "#cbd5e1";
                        e.currentTarget.style.backgroundColor = "#f8fafc";
                      }}
                      onClick={() => document.getElementById("video-logo-upload").click()}
                    >
                      <UploadFileIcon style={{ fontSize: "20px", color: "#64748b" }} />
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <span style={{ fontSize: "13px", color: "#475569" }}>Click to upload logo image</span>
                        <span style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>PNG, JPG, SVG up to 2MB</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Logo Preview Modal */}
            <Dialog open={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} maxWidth="md" fullWidth>
              <div style={{ position: "relative", padding: "24px", display: "flex", justifyContent: "center", backgroundColor: "#fff" }}>
                <IconButton
                  style={{ position: "absolute", top: "12px", right: "12px", backgroundColor: "#f1f5f9" }}
                  onClick={() => setIsPreviewModalOpen(false)}
                >
                  <CloseIcon />
                </IconButton>
                <img src={logoPreview} alt="Logo Preview Full" style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: "8px" }} />
              </div>
            </Dialog>

            {/* Credits Required Display */}
            <div style={{
              marginTop: "16px",
              fontSize: "14px",
              color: "#1E4DE3",
            }}>
              Credits required: <strong>{CREDITS_REQUIRED}</strong>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions className="dialog-actions">
        <Button onClick={handleCloseDialog} variant="text" className="cancel-btn">
          Cancel
        </Button>

        {/* STEP 1 button */}
        {!sectionsLoaded && (
          <Button
            onClick={handleFetchSections}
            variant="contained"
            className="start-generating-btn"
            disabled={loadingSections}
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "#ccc !important",
                color: "#888 !important",
                cursor: "not-allowed",
                pointerEvents: "none",
              },
            }}
          >
            {loadingSections ? "Fetching Sections…" : "Next →"}
          </Button>
        )}

        {/* STEP 2 button */}
        {sectionsLoaded && (
          <Button
            onClick={handleGenerateClick}
            variant="contained"
            className="start-generating-btn"
            disabled={selectedSections.length === 0}
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "#ccc !important",
                color: "#888 !important",
                cursor: "not-allowed",
                pointerEvents: "none"
              }
            }}
          >
            Start Generating
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
AbstractVideoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  videoDuration: PropTypes.string.isRequired,
  setVideoDuration: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  setLanguage: PropTypes.func.isRequired,
  selectedColor: PropTypes.string.isRequired,
  setSelectedColor: PropTypes.func.isRequired,
  customColor: PropTypes.string,
  setCustomColor: PropTypes.func.isRequired,
  onFileChange: PropTypes.func,
  onStartGenerating: PropTypes.func.isRequired,
  contentLevel: PropTypes.string.isRequired,
  setContentLevel: PropTypes.func.isRequired,
  publicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedSections: PropTypes.array.isRequired,
  setSelectedSections: PropTypes.func.isRequired,
};

export default AbstractVideoDialog;

